import { hasMedicareAccessEntryInURL, hasReferralInURL } from '../config/AssessmentUtils';
import { chooseAssessmentConfig } from '../config/ChooseAssessmentConfig';
import { SharedScreenName } from '../config/FlowTypes';
import { NervaControlScreenName } from '../config/nerva/NervaControlScreenNames';
import { PAY_CONFIG } from '../config/PayConfig';
import { ActionType } from '../store/ActionTypes';
import { store } from '../store/Store';
import { UserSelector } from '../store/user/UserSelector';
import { recordError } from '../util/Errors';
import { isDirectToPayRoute } from '../util/RouterUtils';
import { setupForIntegrationTests } from '../util/SetupForIntegrationTests';
import { StorePersistence } from '../util/StorePersistance';
import { Experiments } from './experiments/Experiments';
import { finalisePendingPayment } from './FinalisePayment';
import { updateReferralStoreStateFromUrl } from './UpdateReferralStoreStateFromUrl';

export async function initializeApp(): Promise<void> {
  await setupForIntegrationTests();

  const { didRestoreStore } = restorePersistedStore();
  if (!didRestoreStore && !isDirectToPayRoute()) logoutUser();

  const [{ didFinalisePayment }] = await Promise.all([
    finalisePendingPayment(),
    updateReferralStoreStateFromUrl(),
    Experiments.initialize().catch(recordError), // Continue even on errors
  ]);

  const hasPendingPayment = didRestoreStore && !didFinalisePayment;

  // When a redirect based payment error occurs, we shouldn't navigate as the page will already
  // have been restored to where they left off (the payment page).
  if (!hasPendingPayment) {
    store.dispatch({
      type: ActionType.SET_PAGE,
      page: didFinalisePayment ? downloadAppPage() : firstFlowConfigPage(),
      resetHistory: !didFinalisePayment,
    });
  }
}

function downloadAppPage(): string {
  const state = store.getState();
  const user = UserSelector.user(state);
  return SharedScreenName[user.setUserPassword ? 'FinishAccount' : 'DownloadApp'];
}

function firstFlowConfigPage(): string {
  if (isDirectToPayRoute()) return PAY_CONFIG.screens[0].screenName;

  const flowConfig = chooseAssessmentConfig();
  if (hasMedicareAccessEntryInURL()) {
    // Referred users must sign in before ProgramOverview (the access grant needs their account);
    // Auth flows login → AiLoading → ProgramOverview. Direct users re-enter just before the
    // flow's own US 65+ branching at LiveFreeNow.
    const screenName = hasReferralInURL()
      ? SharedScreenName.Auth
      : NervaControlScreenName.IfFreeFromIbs;
    if (flowConfig.screens.some((screen) => screen.screenName === screenName)) return screenName;
    // Fall back to the normal flow rather than crash FlowStack on an unknown screen.
    recordError(
      new Error(`medicare-access entry: config '${flowConfig.name}' lacks ${screenName}`)
    );
  }
  return flowConfig.screens[0].screenName;
}

function restorePersistedStore(): { didRestoreStore: boolean } {
  const persistedStore = StorePersistence.get();
  if (persistedStore) {
    store.dispatch({ type: ActionType.RESTORE_STORE, persistedStore });
  }
  StorePersistence.clear();
  return { didRestoreStore: !!persistedStore };
}

function logoutUser(): void {
  store.dispatch({ type: ActionType.LOGOUT_USER });
}
